import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormikContext } from "formik";

import Input from "../../components/form/Input";
import Select from "../../components/form/Select";
import DropdownMenuOptionsDialog from "../../components/DropdownMenuOptionsDialog";
import AdvancedOptionsDialog from "../../components/AdvancedOptionsDialog";

const fieldTypes = [
  ["string", "Text"],
  ["decimal", "Number"],
  ["boolean", "Yes/No Toggle"],
];

export default function ConfigurableFieldFormStep({ disableFieldType }) {
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [defaultValueDialogOpen, setDefaultValueDialogOpen] = useState(false);
  const { values, setFieldValue } = useFormikContext();
  const availableOptions = values.availableOptions || [];
  const showDropdownOptions = ["string", "decimal"].includes(values.fieldType);
  const behaviorSummary = [
    availableOptions.length === 0
      ? "No dropdown options"
      : `${availableOptions.length} option${availableOptions.length === 1 ? "" : "s"}`,
    values.autoAddCustomValues ? "Auto-add enabled" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleSaveOptions = options => {
    setFieldValue("availableOptions", options);
  };

  const handleSaveAdvancedOptions = values => {
    setFieldValue("defaultValue", values.defaultValue);
    setFieldValue("restrictToAvailableOptions", values.restrictToAvailableOptions);
    setFieldValue("autoAddCustomValues", values.autoAddCustomValues);
    setFieldValue("enableBarcodeScanner", values.enableBarcodeScanner);
    setFieldValue("multipleValues", values.multipleValues);
  };

  const handleOpenOptionsDialog = () => {
    setOptionsDialogOpen(true);
  };

  const handleCloseOptionsDialog = () => {
    setOptionsDialogOpen(false);
  };

  const handleOpenDefaultValueDialog = () => {
    setDefaultValueDialogOpen(true);
  };

  const handleCloseDefaultValueDialog = () => {
    setDefaultValueDialogOpen(false);
  };

  return (
    <Box>
      <Typography fontSize={18} fontWeight={400} color="textPrimary" mb={3} align="center">
        Required Information
      </Typography>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Select
            disabled={disableFieldType}
            size="medium"
            name="fieldType"
            label="Field Type"
            options={fieldTypes}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Input size="medium" name="defaultLabel" label="Field Name" showNAButton={false} />
        </Grid>
        {showDropdownOptions && (
          <Grid size={12}>
            <Box
              component="button"
              type="button"
              onClick={handleOpenOptionsDialog}
              display="flex"
              alignItems="center"
              width="100%"
              textAlign="left"
              sx={{
                backgroundColor: "transparent",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                cursor: "pointer",
                p: 2,
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Box flexGrow={1}>
                <Typography color="text.primary" fontWeight={500}>
                  Dropdown & behavior
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {behaviorSummary}
                </Typography>
              </Box>
              <ChevronRightIcon color="action" />
            </Box>
          </Grid>
        )}
      </Grid>
      {optionsDialogOpen && (
        <DropdownMenuOptionsDialog
          fieldType={values.fieldType}
          defaultValue={values.defaultValue}
          onClose={handleCloseOptionsDialog}
          onSave={handleSaveOptions}
          initialOptions={availableOptions}
          onOpenDefaultValueDialog={handleOpenDefaultValueDialog}
        />
      )}
      {defaultValueDialogOpen && (
        <AdvancedOptionsDialog
          initialValues={values}
          options={availableOptions}
          showMultipleValues={values.fieldType === "string"}
          onSave={handleSaveAdvancedOptions}
          onClose={handleCloseDefaultValueDialog}
        />
      )}
    </Box>
  );
}
